import one360 from './one360';
import { getUploadDeadline } from '../uploadDeadline';
import type {
  ConstructionDocumentationFilesResponse,
  ConstructionDocumentationUploadResult,
  DriveUploadedFile,
  DriveUploadResponse,
  DriveFileContent,
} from '../../interfaces/drive';

const opsProjectBasePath = (dealId: string) =>
  `/api/v1/ops/projects/${encodeURIComponent(dealId)}`;

/**
 * Lädt die zentrale Google-Drive-Ablage „Baustellendokumentation“ eines Projekts. Optionaler
 * `subfolder` beschränkt die Liste auf einen Unterordner (z.B. "Zusatz" für freie Uploads,
 * getrennt von Checklisten-Pflichtnachweisen im Wurzelordner) — siehe One360-API-Anforderung
 * docs/One360-API-Anforderung-Baustellendokumentation-Upload.md.
 */
export const getConstructionDocumentationFilesAsync = (
  dealId: string,
  subfolder?: string,
): Promise<ConstructionDocumentationFilesResponse> =>
  one360.get<ConstructionDocumentationFilesResponse>(
    `${opsProjectBasePath(dealId)}/construction-documentation/files`,
    { timeout: 90_000, params: subfolder ? { subfolder } : undefined },
  );

export interface UploadConstructionDocumentationFileOptions {
  /** Unterordner innerhalb von Dokumente/Baustellendoku (z.B. "Zusatz"). */
  subfolder?: string;
  /** Wird als Drive-`description`-Property am File gesetzt. */
  description?: string;
}

/** Legt eine Datei im Google-Drive-Ordner Dokumente/Baustellendoku des Projekts ab. */
export const uploadConstructionDocumentationFileAsync = (
  dealId: string,
  dealName: string,
  file: File,
  options: UploadConstructionDocumentationFileOptions = {},
): Promise<ConstructionDocumentationUploadResult> => {
  const formData = new FormData();
  formData.append('File', file);
  formData.append('DealName', dealName);
  if (options.subfolder) {
    formData.append('Subfolder', options.subfolder);
  }
  if (options.description !== undefined) {
    formData.append('Description', options.description);
  }
  return one360.post<ConstructionDocumentationUploadResult>(
    `${opsProjectBasePath(dealId)}/construction-documentation/files`,
    formData,
    { timeout: 90_000 },
  );
};

interface One360FileResult {
  fileId: string;
  fileName: string;
  webViewLink?: string;
  folderId?: string;
  path?: string;
}

export interface UploadDestinationOptions {
  sharedDrive?: string;
  gdriveFolderId?: string;
}

// ADO #1506: Pro Zielordner läuft höchstens ein "Ordner-Anleger" gleichzeitig. Mehrere
// parallele Uploads in einen noch nicht existierenden Ordner (Wizard UND Queue-Verarbeitung,
// siehe queueService.processQueue) würden sonst gleichnamige Drive-Ordner doppelt anlegen.
// Gate = Promise, das nach Abschluss des laufenden Uploads true (Erfolg => Ordner existiert)
// oder false (Fehler => Ordner evtl. nicht angelegt) liefert. Wartende Uploads laufen bei
// Erfolg parallel weiter; bei Fehler rückt genau einer als neuer Anleger nach. Kein
// dauerhafter Cache: ein gelöschter Ordner wird beim nächsten Upload wieder sauber angelegt.
const folderCreationGates = new Map<string, Promise<boolean>>();

// Die Wartezeit zählt nicht zur 30s-Frist des Queue-Items (siehe uploadDeadline.ts): die Frist
// wird während des Wartens pausiert und startet mit vollem Budget, sobald der Upload tatsächlich
// beginnt. Hängt der Anleger, läuft dessen eigene Frist ab und gibt das Gate frei.
async function withFolderCreationGate<T>(
  key: string,
  run: () => Promise<T>,
  signal?: AbortSignal,
): Promise<T> {
  const deadline = getUploadDeadline(signal);
  for (;;) {
    const pending = folderCreationGates.get(key);
    if (!pending) break;
    deadline?.pause();
    const ok = await pending;
    deadline?.resume();
    if (ok) return run();
  }
  const upload = run();
  const release = (ok: boolean): boolean => {
    folderCreationGates.delete(key);
    return ok;
  };
  folderCreationGates.set(key, upload.then(() => release(true), () => release(false)));
  return upload;
}

async function postOneFile(
  file: File,
  dealName: string,
  directories: string[],
  destination: UploadDestinationOptions = {},
  signal?: AbortSignal,
  projectId?: string,
): Promise<One360FileResult> {
  const { sharedDrive, gdriveFolderId } = destination;
  const formData = new FormData();
  formData.append('file', file);
  // dealName is always sent (even when gdriveFolderId is set) so the backend can
  // self-heal by name-based folder resolution if the cached folder id is stale.
  formData.append('dealName', dealName);
  // PR-Review-Finding (Ops-App-PR #9/one360-api-PR #475/#476): ohne die dealId kann das Backend
  // fuer Checklisten-Dokumente ohne eigene HubSpot-Property (z.B. "aussenfuehler",
  // skipHubspotPatch: true) keine dauerhafte, projektbezogene Bestaetigung ablegen - siehe
  // queueService.processDocumentUpload, das diese Funktion mit der aus phaseId abgeleiteten
  // dealId aufruft.
  if (projectId) formData.append('projectId', projectId);
  if (gdriveFolderId) formData.append('folderId', gdriveFolderId);
  directories.forEach(dir => formData.append('directories', dir));
  if (sharedDrive) formData.append('sharedDrive', sharedDrive);
  return one360.post<One360FileResult>('/api/v1/google/drive/upload', formData, { signal });
}

function uploadOneFile(
  file: File,
  dealName: string,
  directories: string[],
  destination: UploadDestinationOptions = {},
  signal?: AbortSignal,
  projectId?: string,
): Promise<One360FileResult> {
  const { sharedDrive, gdriveFolderId } = destination;
  const gateKey = JSON.stringify([dealName, sharedDrive ?? '', gdriveFolderId ?? '', directories]);
  return withFolderCreationGate(gateKey, () =>
    postOneFile(file, dealName, directories, destination, signal, projectId),
    signal,
  );
}

export async function uploadFilesAsync(
  files: File[],
  dealName: string,
  directories: string[] = ['Dokumente', 'Allgemeine Uploads'],
  destination: UploadDestinationOptions = {},
): Promise<DriveUploadResponse> {
  const uploaded: DriveUploadedFile[] = [];
  const errors: string[] = [];

  // ADO #1506: Alle Dateien teilen dasselbe Ziel - die Serialisierung des ersten Uploads
  // (Ordner-Anlage) übernimmt withFolderCreationGate in uploadOneFile.
  const results = await Promise.allSettled(
    files.map((file) => uploadOneFile(file, dealName, directories, destination))
  );

  results.forEach((result, index) => {
    const file = files[index];
    if (result.status === 'fulfilled') {
      const uploadResult = result.value;
      uploaded.push({
        fileId: uploadResult.fileId,
        fileName: uploadResult.fileName,
        webViewLink: uploadResult.webViewLink ?? `https://drive.google.com/file/d/${uploadResult.fileId}`,
      });
    } else {
      console.error('[Drive] uploadFiles fehlgeschlagen:', result.reason);
      errors.push(`${file.name}: ${result.reason?.message ?? result.reason}`);
    }
  });

  return {
    message: errors.length ? 'Einige Dateien konnten nicht hochgeladen werden.' : 'Upload erfolgreich.',
    status: errors.length === 0,
    errors,
    files: uploaded.length ? uploaded : null,
  };
}

export async function uploadSingleFileAsync(
  imageData: string,
  fileName: string,
  dealName: string,
  directories?: string[],
  destination: UploadDestinationOptions = {},
  signal?: AbortSignal,
  projectId?: string,
): Promise<{ fileId: string; fileName: string; webViewLink: string; thumbnailLink?: string } | null> {
  const base64Response = await fetch(imageData);
  const blob = await base64Response.blob();

  try {
    const result = await uploadOneFile(
      new File([blob], fileName, { type: blob.type }),
      dealName,
      directories ?? ['Dokumente', 'Allgemeine Uploads'],
      destination,
      signal,
      projectId,
    );
    if (!result?.fileId) return null;
    return {
      fileId: result.fileId,
      // Nutzer-Rückmeldung: der lokal übergebene `fileName` (im Baustellendoku-Upload ein
      // synthetischer "image-<uuid>.jpg"-Platzhalter, siehe queueService.getUploadFileName) muss
      // nicht dem tatsächlichen, von Drive vergebenen Namen entsprechen — die Backend-Antwort
      // spiegelt IMMER den echten Drive-Namen, muss also bevorzugt werden, sonst weicht ein
      // später erzeugter "-markiert"-Name vom echten Original-Namen ab.
      fileName: result.fileName || fileName,
      webViewLink: result.webViewLink || `https://drive.google.com/file/d/${result.fileId}`,
    };
  } catch (err: any) {
    console.error('[Drive] uploadSingleFile fehlgeschlagen:', err);
    throw new Error(`Drive Upload fehlgeschlagen: ${err.message}`);
  }
}

export interface UploadMarkedPhotoResult {
  fileId: string;
  fileName: string;
  webViewLink?: string;
  thumbnailLink?: string;
  description?: string;
}

// Legt eine markierte Foto-Version als EIGENSTÄNDIGE Datei im selben Drive-Ordner ab statt das
// Original zu überschreiben — beide Versionen bleiben erhalten (Ticket #928, Anhang "Foto-
// Beschriftung/-Markierung", per Nutzer-Rückmeldung). `replaceIfExists: true` macht erneutes
// Markieren desselben Fotos idempotent: die vorhandene "-markiert"-Datei wird dann ersetzt statt
// eine weitere Kopie anzulegen — nur das Original wird nie angefasst.
// Ticket #1055: für Rohranschluss-Fotos ist der echte Zielordner nur als Drive-FolderId bekannt
// (aus GetDriveFileMetadataAsync im Backend), nicht als Directories-Pfad unter dem Deal-Root —
// StorageUploadRequest.FolderId lässt das Backend die Directories-Auflösung überspringen und
// direkt in den bekannten Ordner hochladen.
export async function uploadMarkedPhotoAsync(
  projectId: string,
  target: string[] | { folderId: string },
  imageData: string,
  fileName: string,
  caption?: string,
): Promise<UploadMarkedPhotoResult> {
  const base64Response = await fetch(imageData);
  const blob = await base64Response.blob();
  const formData = new FormData();
  formData.append('file', new File([blob], fileName, { type: blob.type }));
  formData.append('projectId', projectId);
  if (Array.isArray(target)) {
    target.forEach((dir) => formData.append('directories', dir));
  } else {
    formData.append('folderId', target.folderId);
  }
  formData.append('replaceIfExists', 'true');
  // Bewusst `!== undefined` statt Truthy-Check: das Backend interpretiert ein fehlendes
  // `description`-Feld als "nicht ändern" — ein per Editor komplett geleertes Feld (caption === '')
  // muss deshalb trotzdem gesendet werden, sonst bleibt die alte Beschriftung in Drive bestehen
  // (Autoreview-Finding).
  if (caption !== undefined) {
    formData.append('description', caption);
  }
  try {
    // Ticket #1055 (Security-Review-Finding P1): eigener, serverseitig auf Ops-Manager
    // beschränkter Endpoint statt des generischen /drive/upload — der generische Endpoint prüfte
    // keine Rolle, jeder eingeloggte Nutzer hätte per direktem API-Call eine Markierung anlegen
    // können. Siehe One360.Api/Controllers/GoogleResourceController.cs (UploadMarkedPhoto) +
    // EnsureOpsManagerCanMarkPhotoCommand im Backend.
    return await one360.post<UploadMarkedPhotoResult>('/api/v1/google/drive/upload/mark-photo', formData);
  } catch (err) {
    console.error('[Drive] uploadMarkedPhoto fehlgeschlagen:', err);
    const message = err instanceof Error ? err.message : String(err);
    throw new Error(`Drive Upload fehlgeschlagen: ${message}`);
  }
}

export interface DriveFileMetadata {
  id: string;
  name: string;
  mimeType?: string;
  webViewLink?: string;
  thumbnailLink?: string;
  description?: string;
}

// Ticket #1055 (Nutzer-Rückmeldung): Der lokal gecachte Dateiname eines Originals kann vom
// tatsächlichen Drive-Namen abweichen (z.B. eine reine HubSpot-URL-Referenz ohne `name`-Feld,
// oder Drive vergibt beim Upload einen eigenen, generierten Namen) — vor dem Ableiten eines
// "-markiert"-Dateinamens muss deshalb der ECHTE, aktuelle Drive-Name abgefragt werden, statt
// sich auf lokal gecachte Metadaten zu verlassen.
export async function getDriveFileMetadataAsync(fileId: string): Promise<DriveFileMetadata | null> {
  try {
    return await one360.get<DriveFileMetadata>(
      `/api/v1/google/drive/file/${encodeURIComponent(fileId)}/metadata`
    );
  } catch (err) {
    console.error('[Drive] getDriveFileMetadata fehlgeschlagen:', err);
    return null;
  }
}

export async function getFileAsync(fileId: string): Promise<DriveFileContent | null> {
  try {
    const response = await one360.get<Blob>(
      `/api/v1/google/drive/file/${encodeURIComponent(fileId)}`,
      { responseType: 'blob' }
    );
    const blob = response as unknown as Blob;
    const base64Content = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    return { base64Content, mimeType: blob.type };
  } catch (err: any) {
    console.error('[Drive] getFile fehlgeschlagen:', err);
    return null;
  }
}

// Browser-<img src="..."> kann Googles thumbnailLink nicht direkt laden — die URL ist nur mit
// dem Auth-Kontext gültig, mit dem sie geholt wurde (Service-Account), nicht mit einem anonymen
// Browser-Request (Firefox blockt das per Opaque Response Blocking, NS_BINDING_ABORTED). Diese
// Funktion holt die Thumbnail-Bytes stattdessen über unser Backend (Ticket #928).
export async function getThumbnailAsync(
  fileId: string,
  thumbnailLink?: string
): Promise<DriveFileContent | null> {
  try {
    const query = thumbnailLink ? `?thumbnailLink=${encodeURIComponent(thumbnailLink)}` : '';
    const response = await one360.get<Blob>(
      `/api/v1/google/drive/file/${encodeURIComponent(fileId)}/thumbnail${query}`,
      { responseType: 'blob' }
    );
    const blob = response as unknown as Blob;
    const base64Content = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    return { base64Content, mimeType: blob.type };
  } catch (err) {
    console.error('[Drive] getThumbnail fehlgeschlagen:', err);
    return null;
  }
}

export async function deleteFileAsync(fileId: string): Promise<void> {
  try {
    await one360.delete(`/api/v1/google/drive/file/${encodeURIComponent(fileId)}`);
  } catch (err: any) {
    console.error('[Drive] deleteFile fehlgeschlagen:', err);
    throw new Error(`Drive Delete fehlgeschlagen: ${err.message}`);
  }
}

/** Dekodiert einen Base64-String (ohne "data:...;base64," Prefix) in ein Byte-Array. */
export function base64ToBytes(base64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export function dataUrlToFile(dataUrl: string, fileName: string): File {
  const [meta, base64] = dataUrl.split(',');
  const mime = meta.match(/:(.*?);/)?.[1] || 'image/jpeg';
  return new File([base64ToBytes(base64)], fileName, { type: mime });
}
